export function messageKeys(message) {
  return [message?.id, message?.messageUid, message?.raw?.clientMsgID, message?.raw?.serverMsgID]
    .filter(Boolean)
    .map(value => String(value));
}

export function isRecalledMessage(message) {
  return Boolean(message?.recalled) || message?.status === 'recalled';
}

export function shouldHideRecalledMessage(message) {
  return isRecalledMessage(message) && message?.direction !== 'out';
}

function hasOriginalContent(message) {
  const text = String(message?.text || '').trim();
  return Boolean(message?.attachment)
    || Boolean(text && text !== '[已撤回]' && text !== '已撤回');
}

function originalContentSource(...messages) {
  return messages.find(message => message?.direction === 'out' && hasOriginalContent(message))
    || messages.find(hasOriginalContent)
    || messages.find(Boolean)
    || {};
}

export function withRecalledState(message, originalMessage = message) {
  const content = originalContentSource(originalMessage, message);
  return {
    ...message,
    type: content.type || message?.type || 'text',
    text: content.text ?? message?.text ?? '',
    attachment: content.attachment ?? message?.attachment ?? null,
    status: 'recalled',
    deliveryStatus: '',
    recalled: true,
    recallStatus: message?.recallStatus || '已撤回'
  };
}

export function preserveRecalledState(existingMessage, incomingMessage) {
  const merged = { ...existingMessage, ...incomingMessage };
  return isRecalledMessage(existingMessage) || isRecalledMessage(incomingMessage)
    ? withRecalledState(merged, originalContentSource(existingMessage, incomingMessage))
    : merged;
}

function messageTimestamp(message) {
  return Number(message?.displayTimestamp || message?.timestamp || message?.serverTimestamp || 0);
}

function isSenderRecallPair(left, right) {
  if (left?.direction !== 'out' || right?.direction !== 'out') {
    return false;
  }
  if (isRecalledMessage(left) === isRecalledMessage(right)) {
    return false;
  }
  const leftKeys = messageKeys(left);
  const rightKeys = messageKeys(right);
  if (leftKeys.some(key => rightKeys.includes(key))) {
    return true;
  }
  const leftTimestamp = messageTimestamp(left);
  const rightTimestamp = messageTimestamp(right);
  return leftTimestamp > 0
    && rightTimestamp > 0
    && Math.abs(leftTimestamp - rightTimestamp) <= 2000
    && hasOriginalContent(isRecalledMessage(left) ? right : left);
}

export function collapseSenderRecallDuplicates(messages) {
  const collapsed = [];
  (messages || []).forEach(message => {
    const pairIndex = collapsed.findIndex(current => isSenderRecallPair(current, message));
    if (pairIndex < 0) {
      collapsed.push(message);
      return;
    }
    const current = collapsed[pairIndex];
    const original = isRecalledMessage(current) ? message : current;
    const recalled = isRecalledMessage(current) ? current : message;
    collapsed.splice(pairIndex, 1, withRecalledState({ ...original, ...recalled }, original));
  });
  return collapsed;
}

export function applyRecallEvent(messagesByConversation, clientMsgID) {
  const targetID = String(clientMsgID || '');
  const matches = [];
  const nextMessages = {};

  Object.entries(messagesByConversation || {}).forEach(([conversationId, messages]) => {
    nextMessages[conversationId] = (messages || []).flatMap(message => {
      if (!targetID || !messageKeys(message).includes(targetID)) {
        return [message];
      }
      if (message?.direction !== 'out') {
        matches.push({ conversationId, message, action: 'hidden' });
        return [];
      }
      const recalledMessage = withRecalledState(message);
      matches.push({ conversationId, message: recalledMessage, action: 'recalled' });
      return [recalledMessage];
    });
  });

  return { messagesByConversation: nextMessages, matches };
}
